// Per-tool visual identity. Each tool gets a single glyph and an
// accent color class. ToolCallRow / PendingRow use these to make the
// event stream read like a pattern grid — you see density, you see
// intent, you recognize operations at a glance.
//
// Glyphs chosen for visual parity (box-width equivalent at monospace
// display sizes) and for rough semantic fit:
//   ◉ solid-dot = create new voice
//   ◈ diamond   = append / augment
//   ◊ hollow    = wrap / transform
//   ✕ x-mark    = remove
//   ✎ pencil    = edit-in-place
//   ⟳ cycle     = loop / rearrange
//   ♪ note      = tonal
//   ▶ play-bar  = transport
//
// Accent classes are Tailwind text-* colors. Same token mapping the
// rest of the UI uses; no new palette.

type ToolStyle = {
  glyph: string;
  /** Tailwind text-color class (e.g. 'text-emerald-400'). Used for the
   *  glyph and the applied-row flash accent. */
  accent: string;
  /** Matching ring-color class for the flash border. */
  ring: string;
};

// Union of every tool name the agent knows about. Kept in sync with
// the tools registered in tools.ts — exhaustive record below means
// renaming a tool there without updating this file is a compile error.
export type KnownToolName =
  | 'get_song'
  | 'get_block'
  | 'get_transport_state'
  | 'list_effect_kinds'
  | 'list_sample_banks'
  | 'add_voice'
  | 'remove_block'
  | 'set_pattern'
  | 'add_effect'
  | 'remove_effect'
  | 'update_effect_arg'
  | 'wrap_in_conditional'
  | 'wrap_in_stack'
  | 'wrap_in_arrange'
  | 'set_tempo'
  | 'rename_const'
  | 'add_comment'
  | 'play'
  | 'stop'
  | 'set_solo';

const BY_NAME: Record<KnownToolName, ToolStyle> = {
  // Read
  get_song: { glyph: '⌕', accent: 'text-zinc-400', ring: 'ring-zinc-700' },
  get_block: { glyph: '⌕', accent: 'text-zinc-400', ring: 'ring-zinc-700' },
  get_transport_state: {
    glyph: '⌕',
    accent: 'text-zinc-400',
    ring: 'ring-zinc-700',
  },
  list_effect_kinds: {
    glyph: '⌕',
    accent: 'text-zinc-400',
    ring: 'ring-zinc-700',
  },
  list_sample_banks: {
    glyph: '⌕',
    accent: 'text-zinc-400',
    ring: 'ring-zinc-700',
  },
  // Compose (song-mutating)
  add_voice: {
    glyph: '◉',
    accent: 'text-emerald-400',
    ring: 'ring-emerald-400/60',
  },
  remove_block: {
    glyph: '✕',
    accent: 'text-rose-400',
    ring: 'ring-rose-400/60',
  },
  set_pattern: {
    glyph: '✎',
    accent: 'text-cyan-300',
    ring: 'ring-cyan-300/60',
  },
  add_effect: {
    glyph: '◈',
    accent: 'text-cyan-300',
    ring: 'ring-cyan-300/60',
  },
  remove_effect: {
    glyph: '✕',
    accent: 'text-rose-400',
    ring: 'ring-rose-400/60',
  },
  update_effect_arg: {
    glyph: '✎',
    accent: 'text-cyan-300',
    ring: 'ring-cyan-300/60',
  },
  wrap_in_conditional: {
    glyph: '◊',
    accent: 'text-fuchsia-400',
    ring: 'ring-fuchsia-400/60',
  },
  wrap_in_stack: {
    glyph: '◊',
    accent: 'text-fuchsia-400',
    ring: 'ring-fuchsia-400/60',
  },
  wrap_in_arrange: {
    glyph: '⟳',
    accent: 'text-fuchsia-400',
    ring: 'ring-fuchsia-400/60',
  },
  set_tempo: {
    glyph: '♪',
    accent: 'text-amber-300',
    ring: 'ring-amber-300/60',
  },
  rename_const: {
    glyph: '✎',
    accent: 'text-cyan-300',
    ring: 'ring-cyan-300/60',
  },
  add_comment: {
    glyph: '✎',
    accent: 'text-zinc-400',
    ring: 'ring-zinc-600',
  },
  // Transport
  play: { glyph: '▶', accent: 'text-yellow-300', ring: 'ring-yellow-300/60' },
  stop: { glyph: '■', accent: 'text-yellow-300', ring: 'ring-yellow-300/60' },
  set_solo: {
    glyph: '○',
    accent: 'text-yellow-300',
    ring: 'ring-yellow-300/60',
  },
};

const FALLBACK: ToolStyle = {
  glyph: '·',
  accent: 'text-zinc-400',
  ring: 'ring-zinc-700',
};

export function toolStyleFor(name: string): ToolStyle {
  // Narrow at runtime — traces arrive as plain strings from the
  // Anthropic response. The record is exhaustive over KnownToolName
  // so a correct-at-compile-time entry is guaranteed to hit.
  return (BY_NAME as Record<string, ToolStyle>)[name] ?? FALLBACK;
}
